import { useEffect, useRef, useState } from 'react';
import type { BookGenre } from '@booknest/shared';

import { Input } from '@/shared/components/Input.js';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue.js';

import { useBookSearch } from '../hooks/useBookSearch.js';

import { BookSuggestionRow } from './BookSuggestionRow.js';

interface LibrarySearchProps {
  genre?: BookGenre;
}

export function LibrarySearch({ genre }: LibrarySearchProps = {}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const debounced = useDebouncedValue(query, 250);
  const { data, isFetching, isPlaceholderData, error } = useBookSearch(
    debounced,
    genre,
  );

  const trimmed = debounced.trim();
  const hasResults = Boolean(data && data.items.length > 0);
  const showDropdown = open && (trimmed.length >= 2 || hasResults);

  useEffect(() => {
    if (!showDropdown) return;
    const onClick = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showDropdown]);

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-serif text-xl font-semibold text-brown">
        Cherche un livre 🌿
      </h2>

      <div ref={containerRef} className="relative">
        <Input
          id="book-search"
          placeholder="Titre, auteur, ISBN…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {isFetching && trimmed.length >= 2 && (
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-brown-soft">
            …
          </span>
        )}

        {showDropdown && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-96 overflow-y-auto rounded-2xl border border-cream-dark bg-paper shadow-lg">
            {error && (
              <p className="p-3 text-sm text-red-500">
                Catalogue indisponible. Réessaie dans un instant.
              </p>
            )}
            {!error && trimmed.length < 2 && (
              <p className="p-3 text-xs text-brown-soft">
                Tape encore une lettre pour lancer la recherche.
              </p>
            )}
            {!error && trimmed.length >= 2 && hasResults && data && (
              <ul
                className={`divide-y divide-cream-dark transition-opacity ${
                  isPlaceholderData ? 'opacity-60' : 'opacity-100'
                }`}
              >
                {data.items.map((book) => (
                  <BookSuggestionRow
                    key={book.id}
                    book={book}
                    onAdded={() => setOpen(false)}
                  />
                ))}
              </ul>
            )}
            {!error &&
              trimmed.length >= 2 &&
              !isFetching &&
              data &&
              data.items.length === 0 && (
                <p className="p-3 text-sm text-brown-soft">
                  Aucun résultat pour « {trimmed} ».
                </p>
              )}
            {!error && trimmed.length >= 2 && isFetching && !hasResults && (
              <p className="p-3 text-sm text-brown-soft">Recherche en cours…</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
